import type { Agent } from '@/subsystems/agents'
import type { DocumentListItem, PipelineStatus } from '@/subsystems/documents'

import type { SrmRequest } from '../types'

export interface SrmRunPlan {
  request: SrmRequest
  agent: Agent
  documentIds: string[]
}

const ACTIVE_STATUSES = new Set(['pending', 'queued', 'running'])

function documentsForAgent(request: SrmRequest, agent: Agent): DocumentListItem[] {
  const slotIds = new Set(agent.slots.map(slot => slot.id))
  return request.documents.filter(document => (
    document.slotId !== null && slotIds.has(document.slotId)
  ))
}

function statusForAgent(
  document: DocumentListItem,
  agent: Agent,
): PipelineStatus | undefined {
  return document.pipelineStatuses?.find(status => (
    status.agentId
      ? status.agentId === agent.agentId
      : status.pipelineId === agent.pipelineId
  ))
}

function statusIsCurrent(
  document: DocumentListItem,
  status: PipelineStatus | undefined,
): boolean {
  if (status?.status.toLowerCase() !== 'completed') return false
  if (!status.updatedAt) return true
  return Date.parse(status.updatedAt) >= Date.parse(document.latestVersionAt)
}

/**
 * Return one runnable analysis per request and agent.
 *
 * Only connector-backed requests participate. A changed document makes its
 * whole agent track pending because SRM renderers aggregate every document in
 * that track. Active tracks are excluded so a second dashboard action cannot
 * queue the same work.
 */
export function pendingConnectorRuns(
  requests: SrmRequest[],
  agents: Agent[],
): SrmRunPlan[] {
  return requests.flatMap(request => {
    if (!request.documents.some(document => document.sourceProvider !== null)) return []

    return agents.flatMap(agent => {
      if (!agent.pipelineId) return []
      const documents = documentsForAgent(request, agent)
      if (documents.length === 0) return []
      const statuses = documents.map(document => statusForAgent(document, agent))
      if (statuses.some(status => ACTIVE_STATUSES.has(status?.status.toLowerCase() ?? ''))) {
        return []
      }
      if (documents.every((document, index) => statusIsCurrent(document, statuses[index]))) {
        return []
      }
      return [{
        request,
        agent,
        documentIds: documents.map(document => document.documentId),
      }]
    })
  })
}
